import React, { ReactElement, SyntheticEvent, useContext, useEffect, useRef, useState } from "react";
import { Alert, Box, Checkbox, FormControl, FormControlLabel, FormGroup, IconButton, Link, MenuItem, Select, SelectChangeEvent, TextField, Typography, styled } from "@mui/material";
import { DeleteForever as DeleteForeverIcon } from "@mui/icons-material";
import { useParams } from "react-router-dom";
import { RoomParams } from "../types/RouterTypes";
import { HttpClient } from "../api/HttpClient";
import { useAlert } from "../contexts/alert/useAlert";
import { PlayingCardDTO } from "../api/DTO";
import { Category, Action } from "../analytics/Tracking";
import { CardDeckType, RoomResponse, UpdatePlayingCardsRequest } from "../api/Requests";
import { UserContext } from "../contexts/UserContext";
import { PlanStatus, PlanType } from "../api/models/User";
import { LoadingButton } from "../components/LoadingButton";
import { useTracking } from "../analytics/useTracking";
import { PreferencesService } from "../services/PreferencesService";

class PlayingCardViewModel {
    value: string;
    order: number;
    isEnabled: boolean;

    constructor(value: string, order: number, isEnabled: boolean) {
        this.value = value;
        this.order = order;
        this.isEnabled = isEnabled;
    }
}

const StyledAlert = styled(Alert)({
    width: '100%',
    maxWidth: '300px',
    boxSizing: 'border-box',
    minWidth: 0,
    wordWrap: 'break-word',
    overflowWrap: 'break-word',
    '& .MuiAlert-message': {
        width: '100%',
        minWidth: 0,
        wordWrap: 'break-word',
        overflowWrap: 'break-word',
        overflow: 'hidden'
    }
});

interface CardDeckSettingsProps {
    onSaved?: () => void;
}

export function CardDeckSettings(props: CardDeckSettingsProps): ReactElement {
    const [cardDeckType, setCardDeckType] = React.useState(CardDeckType.Unknown);
    const [cards, setCards] = React.useState<PlayingCardViewModel[]>([]);
    const { id: roomUrlId } = useParams<RoomParams>();
    const alert = useAlert();
    const { track } = useTracking();
    const [isLoading, setIsLoading] = useState(false);
    const { user, authToken } = useContext(UserContext);
    const hasActiveSubscription = (user?.plan.type == PlanType.Premium || user?.plan.type == PlanType.Trial) && user?.plan.status == PlanStatus.Active;
    const lockedPostFix = !hasActiveSubscription ? " (🔒 business plan)" : "";
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
    const [focusIndex, setFocusIndex] = useState<number | null>(null);
    const savedCustomCards = useRef<Array<{ order: number; value: string }> | null>(null);

    //TODO pri3: could be initialized from the backend to keep it even DRY'er
    const scrumScale = ["0", "1/2", "1", "2", "3", "5", "8", "13", "20", "40", "100", "?", "☕"];
    const fibonacciScale = ["0", "1", "2", "3", "5", "8", "13", "21", "34", "55", "89", "?", "☕"];
    const powersOfTwoScale = ["0", "1", "2", "4", "8", "16", "32", "64", "?", "☕"];
    const tShirtScale = ["XXS", "XS", "S", "M", "L", "XL", "XXL", "?", "☕"];

    const MAX_CUSTOM_CARDS = 16;

    /**
     * Checks if a card value is calculable (numeric or "1/2").
     * A card is calculable only if it's purely numeric (no letters or other non-numeric characters).
     * This prevents mixed content like "1aa" from being considered calculable.
     * 
     * @param value - The card value to check
     * @returns true if the card is calculable, false otherwise
     */
    function isCalculableCard(value: string): boolean {
        if (value === "1/2") return true;
        if (value === "?" || value === "☕" || value === "☕️" || value === "♾️") return false;
        
        // Trim whitespace
        const trimmed = value.trim();
        if (trimmed === "") return false;
        
        // Check if the entire string is numeric (allows decimal points and negative signs)
        // Use regex to ensure it's purely numeric: optional minus, digits, optional decimal point and more digits
        // This prevents "1aa", "abc", "12.5abc" etc. from being considered calculable
        const numericRegex = /^-?\d+(\.\d+)?$/;
        if (!numericRegex.test(trimmed)) {
            return false;
        }
        
        // Double-check with parseFloat to ensure it's a valid number
        const num = parseFloat(trimmed);
        return !isNaN(num) && isFinite(num);
    }

    /**
     * Checks if a custom deck supports alignment score calculation.
     * Alignment scores require at least one calculable card and no "?" cards.
     * 
     * @param cards - Array of card view models to check
     * @returns true if the deck supports alignment scores, false otherwise
     */
    function supportsAlignmentScores(cards: PlayingCardViewModel[]): boolean {
        const enabledCards = cards.filter(c => c.isEnabled);
        if (enabledCards.length === 0) return false;
        
        // If any card is "?", alignment score cannot be calculated
        if (enabledCards.some(c => c.value === "?")) return false;
        
        // Check if at least one card is calculable
        return enabledCards.some(c => isCalculableCard(c.value));
    }

    /**
     * Saves current custom cards to ref before switching away from Custom deck type.
     * This allows restoring the cards if user switches back to Custom without saving.
     */
    function saveCustomCardsBeforeSwitch() {
        if (cardDeckType === CardDeckType.Custom && cards.length > 0) {
            const customCardsToSave = cards
                .filter(c => c.isEnabled && c.value && c.value.trim() !== "")
                .map((card, index) => ({ order: index, value: card.value.trim() }));
            if (customCardsToSave.length > 0) {
                savedCustomCards.current = customCardsToSave;
            }
        }
    }

    const onCardDeckTypeChanged = (event: SelectChangeEvent<CardDeckType>) => {
        const newType = event.target.value as CardDeckType;
        
        // Save current custom cards before switching away from Custom deck
        saveCustomCardsBeforeSwitch();
        
        setCardDeckType(newType);
        updateCards(newType);
    };

    function updateCards(
        type: CardDeckType,
        cards?: Array<{
            order: number;
            value: string;
        }>
    ) {
        const cardViewModels: PlayingCardViewModel[] = [];
        let scale: string[] = [];

        if (type == CardDeckType.Scrum) {
            scale = scrumScale;
        } else if (type == CardDeckType.Fibonacci) {
            scale = fibonacciScale;
        } else if (type == CardDeckType.PowersOfTwo) {
            scale = powersOfTwoScale;
        } else if (type == CardDeckType.TShirt) {
            scale = tShirtScale;
        } else if (type == CardDeckType.Custom) {
            // For custom decks, restore saved custom cards if available, otherwise use provided cards
            const cardsToUse = savedCustomCards.current || cards;
            if (cardsToUse && cardsToUse.length > 0) {
                // Load existing custom cards
                for (let i = 0; i < cardsToUse.length; i++) {
                    cardViewModels.push(new PlayingCardViewModel(cardsToUse[i].value, cardsToUse[i].order, true));
                }
            }
            // If no cards provided, start with empty array (user will add cards)
            setCards(cardViewModels);
            track.event(Category.Moderation, Action.CardDeckTypeChanged, type.toString());
            return;
        }

        for (let i = 0; i < scale.length; i++) {
            cardViewModels.push(new PlayingCardViewModel(scale[i], i, cards == undefined ? true : cards?.find((c) => c.value === scale[i]) ? true : false));
        }

        setCards(cardViewModels);
        track.event(Category.Moderation, Action.CardDeckTypeChanged, type.toString());
    }

    function handleAddCard() {
        if (cards.length >= MAX_CUSTOM_CARDS) {
            alert.showMessage(`Maximum ${MAX_CUSTOM_CARDS} cards allowed`, "warning");
            return;
        }
        const newCard = new PlayingCardViewModel("", cards.length, true);
        const newIndex = cards.length;
        setCards([...cards, newCard]);
        // Set focus index to trigger focus in useEffect
        setFocusIndex(newIndex);
    }

    function handleDeleteCard(index: number) {
        const newCards = cards.filter((_, i) => i !== index);
        // Reorder cards
        newCards.forEach((card, i) => {
            card.order = i;
        });
        setCards(newCards);
    }

    function handleCardValueChange(index: number, value: string) {
        // Limit to 6 characters (numbers or emojis)
        if (value.length > 6) {
            alert.showMessage("Card value cannot exceed 6 characters", "warning");
            return;
        }
        const newCards = [...cards];
        newCards[index].value = value;
        setCards(newCards);
    }

    function handleKeyDown(event: React.KeyboardEvent, index: number) {
        if (event.key === 'Enter') {
            event.preventDefault();
            // Only add new card if not at max and current card has a value
            if (cards.length < MAX_CUSTOM_CARDS && cards[index].value.trim() !== '') {
                handleAddCard();
            }
        }
    }

    useEffect(() => {
        const fetchData = async () => {
            try {
                setIsLoading(true);
                const httpClient = new HttpClient();
                const response = (await httpClient.get("/rooms/" + roomUrlId)) as RoomResponse;
                const cards = response.playingCards;

                // Save custom cards if the deck type is Custom
                if (response.cardDeckType === CardDeckType.Custom && cards && cards.length > 0) {
                    savedCustomCards.current = cards;
                }

                setCardDeckType(response.cardDeckType);
                updateCards(response.cardDeckType, cards);

                setIsLoading(false);
            } catch (error) {
                setIsLoading(false);
                // Error details are logged by HttpClient/network layer
                // User-friendly message is shown via alert
                alert.showMessage("Not able to initialize card deck, please try again 🤞", "error");
            }
        };
        fetchData();
    }, []);

    // Focus on input field when a new card is added
    useEffect(() => {
        if (focusIndex !== null && inputRefs.current[focusIndex]) {
            inputRefs.current[focusIndex]?.focus();
            setFocusIndex(null);
        }
    }, [focusIndex, cards.length]);

    async function handleSave(event: SyntheticEvent) {
        event.preventDefault();

        // Validation
        if (cardDeckType === CardDeckType.Custom) {
            const enabledCards = cards.filter(c => c.isEnabled);
            if (enabledCards.length === 0) {
                alert.showMessage("At least one card must be enabled", "error");
                return;
            }
            // Check for empty card values
            const emptyCards = enabledCards.filter(c => !c.value || c.value.trim() === "");
            if (emptyCards.length > 0) {
                alert.showMessage("Card values cannot be empty", "error");
                return;
            }
            // Check for card values exceeding 6 characters
            const longCards = enabledCards.filter(c => c.value.trim().length > 6);
            if (longCards.length > 0) {
                alert.showMessage("Card values cannot exceed 6 characters", "error");
                return;
            }
            // Check for duplicate card values
            const cardValues = enabledCards.map(c => c.value.trim().toLowerCase());
            const uniqueValues = new Set(cardValues);
            if (cardValues.length !== uniqueValues.size) {
                alert.showMessage("Card values must be unique", "error");
                return;
            }
        }

        setIsLoading(true);
        const httpClient = new HttpClient();
        try {
            if (!roomUrlId) {
                throw new Error("Room URL ID is required");
            }
            const request = new UpdatePlayingCardsRequest(roomUrlId);
            request.cardDeckType = cardDeckType;
            
            if (cardDeckType === CardDeckType.Custom) {
                // For custom decks, use all cards (enabled/disabled doesn't apply the same way)
                // But we still filter to only enabled cards for consistency
                request.playingCards = cards
                    .filter((card) => card.isEnabled && card.value && card.value.trim() !== "")
                    .map((card, index) => new PlayingCardDTO(index, card.value.trim()));
            } else {
                request.playingCards = cards.filter((cards) => cards.isEnabled).map((card) => new PlayingCardDTO(card.order, card.value));
            }

            await httpClient.post("/rooms/updatePlayingCards", request);
            
            // Save preferences using PreferencesService (non-blocking)
            // Don't fail card deck update if preference save fails
            try {
                const preferencesService = new PreferencesService(authToken);
                await preferencesService.savePreferences(
                    request.cardDeckType,
                    request.playingCards,
                    user
                );
            } catch (preferencesError) {
                // Silently continue - preferences save failure is non-critical
                // Card deck update succeeded, which is the primary operation
                // Preferences can be saved on next attempt
            }

            const cardDeck = request.playingCards.map<string>((card) => card.value);
            track.event(Category.Moderation, Action.PlayingCardsUpdated, cardDeck.toString());

            setIsLoading(false);
            if (props.onSaved) {
                props.onSaved();
            }
        } catch (error) {
            setIsLoading(false);
            // Error details are logged by HttpClient/network layer
            // User-friendly message is shown via alert
            alert.showMessage("Not able to update card deck, please try again 🤞", "error");
        }
    }

    //TODO pri3: Select label not shown when applied label="Card Deck Type"
    return (
        <Box sx={{ padding: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, width: '100%', maxWidth: '100%', boxSizing: 'border-box', overflow: 'hidden' }}>
            <div>
                <Typography align="center" variant="h5">
                    Configure Card Deck
                </Typography>
            </div>
            <div>
                <Select value={cardDeckType} onChange={onCardDeckTypeChanged}>
                    <MenuItem value={CardDeckType.Scrum}>Scrum scale</MenuItem>
                    <MenuItem value={CardDeckType.Fibonacci} disabled={!hasActiveSubscription}>
                        Fibonacci sequence{lockedPostFix}
                    </MenuItem>
                    <MenuItem value={CardDeckType.PowersOfTwo} disabled={!hasActiveSubscription}>
                        Powers of two{lockedPostFix}
                    </MenuItem>
                    <MenuItem value={CardDeckType.TShirt} disabled={!hasActiveSubscription}>
                        T-Shirt sizes{lockedPostFix}
                    </MenuItem>
                    <MenuItem value={CardDeckType.Custom} disabled={!hasActiveSubscription}>
                        Custom{lockedPostFix}
                    </MenuItem>
                </Select>
            </div>

            {cardDeckType === CardDeckType.Custom ? (
                <Box sx={{ width: '100%', maxWidth: 600, display: 'flex', flexDirection: 'column', gap: 2, boxSizing: 'border-box', minWidth: 0, overflow: 'hidden' }}>
                    {!supportsAlignmentScores(cards) && cards.filter(c => c.isEnabled && c.value).length > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                            <StyledAlert severity="warning">
                                This card deck does not support alignment scores because it only contains non-numeric cards.
                            </StyledAlert>
                        </Box>
                    )}
                    
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {cards.map((card, index) => (
                            <Box 
                                key={index} 
                                sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: { xs: 0.5, sm: 1 },
                                    minWidth: 0,
                                    width: '100%'
                                }}
                            >
                                <Checkbox
                                    color="secondary"
                                    checked={card.isEnabled}
                                    onChange={(event) => {
                                        card.isEnabled = event.target.checked;
                                        setCards([...cards]);
                                    }}
                                    sx={{ flexShrink: 0 }}
                                />
                                <TextField
                                    size="small"
                                    value={card.value}
                                    onChange={(e) => handleCardValueChange(index, e.target.value)}
                                    onKeyDown={(e) => handleKeyDown(e, index)}
                                    placeholder="Card value (max 6 chars)"
                                    fullWidth
                                    sx={{ 
                                        flexGrow: 1,
                                        flexShrink: 1,
                                        flexBasis: 0,
                                        minWidth: 0,
                                        '& .MuiInputBase-root': {
                                            minWidth: 0,
                                        },
                                        '& .MuiInputBase-input': {
                                            minWidth: 0,
                                        }
                                    }}
                                    error={card.value.trim() === "" && card.isEnabled}
                                    inputProps={{ maxLength: 6 }}
                                    inputRef={(el) => {
                                        inputRefs.current[index] = el;
                                    }}
                                />
                                <IconButton
                                    onClick={() => handleDeleteCard(index)}
                                    size="small"
                                    sx={{ flexShrink: 0 }}
                                >
                                    <DeleteForeverIcon />
                                </IconButton>
                            </Box>
                        ))}
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="textSecondary">
                            {cards.length} / {MAX_CUSTOM_CARDS} cards
                        </Typography>
                        <Link
                            component="button"
                            type="button"
                            onClick={handleAddCard}
                            disabled={cards.length >= MAX_CUSTOM_CARDS}
                            title="Add Link"
                            sx={{
                                cursor: cards.length >= MAX_CUSTOM_CARDS ? 'not-allowed' : 'pointer',
                                opacity: cards.length >= MAX_CUSTOM_CARDS ? 0.5 : 1,
                                textDecoration: 'underline',
                                color: 'inherit',
                            }}
                        >
                            Add Card
                        </Link>
                    </Box>
                </Box>
            ) : (
                <div>
                    <FormControl component="fieldset" sx={{ margin: 2 }}>
                        <Box sx={{ columnCount: 3, maxHeight: 240 }}>
                            <FormGroup>
                                {cards.map((card) => [
                                    <FormControlLabel
                                        key={card.order}
                                        sx={{ width: 100 }}
                                        control={
                                            <Checkbox
                                                color="secondary"
                                                checked={card.isEnabled}
                                                onChange={(event) => {
                                                    card.isEnabled = event.target.checked;
                                                    setCards([...cards]);
                                                }}
                                                name={card.value}
                                            />
                                        }
                                        label={
                                            <Typography color="textPrimary" variant="body1">
                                                {card.value}
                                            </Typography>
                                        }
                                    />,
                                ])}
                            </FormGroup>
                        </Box>
                    </FormControl>
                </div>
            )}
            <div>
                <LoadingButton isLoading={isLoading} onClick={handleSave}>
                    Save Card Deck
                </LoadingButton>
            </div>
        </Box>
    );
}
